import React, { useMemo } from 'react';
import { T, panelStyle, GradientTitle, SparkLine, MiniBar, formatNumber } from './NOCShared';
import { formatTime } from '../../utils/formatTime';

function TopologyViz({ nodes = 0, edges = 0, serverList = [], services = [] }) {
  const serverNames = useMemo(() => {
    if (serverList.length > 0) return serverList.map(s => ({
      name: s.name || s.hostname || s.ip_address || 'Server',
      status: s.status || (s.active ? 'online' : 'offline'),
    }));
    return services.map(s => ({ name: s.name, status: s.status || 'online' }));
  }, [serverList, services]);

  const totalNodes = Math.max(nodes, serverList.length, services.length, 6);

  const ringNodes = useMemo(() => {
    const arr = [];
    const cx = 50, cy = 50;
    const outerR = 32;
    for (let i = 0; i < totalNodes; i++) {
      const angle = (i / totalNodes) * Math.PI * 2 - Math.PI / 2;
      const info = serverNames[i] || { name: `Node-${i + 1}`, status: 'online' };
      arr.push({ x: cx + outerR * Math.cos(angle), y: cy + outerR * Math.sin(angle), name: info.name, status: info.status, id: i });
    }
    return arr;
  }, [totalNodes, serverNames]);

  const nodeStatusColor = (status) => {
    switch (status) {
      case 'online': case 'healthy': case 'running': case 'active': return T.success;
      case 'warning': return T.warning;
      case 'offline': case 'exited': case 'error': return '#555';
      default: return T.success;
    }
  };

  const particles = useMemo(() => {
    const p = [];
    for (let i = 0; i < 12; i++) {
      const from = i % totalNodes;
      const to = (totalNodes + Math.floor(totalNodes / 2) + i) % totalNodes;
      if (from !== to && ringNodes[from] && ringNodes[to]) {
        p.push({ from: ringNodes[from], to: ringNodes[to], delay: i * 0.7, id: i });
      }
    }
    return p;
  }, [totalNodes, ringNodes]);

  const bgStars = useMemo(() => {
    const stars = [];
    for (let i = 0; i < 18; i++) {
      stars.push({
        x: 5 + Math.random() * 90,
        y: 5 + Math.random() * 90,
        r: 0.1 + Math.random() * 0.35,
        delay: Math.random() * 5,
        dur: 1.5 + Math.random() * 3,
      });
    }
    return stars;
  }, []);

  const shootingStars = useMemo(() => {
    return [
      { x1: 10, y1: 2, x2: 35, y2: 28, delay: 0, dur: 2.5 },
      { x1: 85, y1: 8, x2: 55, y2: 38, delay: 3, dur: 3 },
      { x1: 5, y1: 90, x2: 45, y2: 55, delay: 6, dur: 2.8 },
      { x1: 95, y1: 75, x2: 55, y2: 38, delay: 9, dur: 3.2 },
    ];
  }, []);

  return (
    <div className="relative w-full" style={{ paddingBottom: '64%' }}>
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="edgeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={T.pink} stopOpacity="0.25" />
            <stop offset="100%" stopColor={T.purple} stopOpacity="0.1" />
          </linearGradient>
          <radialGradient id="neuralGrad" cx="50%" cy="50%">
            <stop offset="0%" stopColor={T.purple} stopOpacity="0.9" />
            <stop offset="50%" stopColor={T.pink} stopOpacity="0.5" />
            <stop offset="100%" stopColor={T.purple} stopOpacity="0.1" />
          </radialGradient>
          <radialGradient id="coreGlow" cx="50%" cy="50%">
            <stop offset="0%" stopColor={T.pink} stopOpacity="0.4" />
            <stop offset="40%" stopColor={T.purple} stopOpacity="0.15" />
            <stop offset="100%" stopColor={T.purple} stopOpacity="0" />
          </radialGradient>
          <filter id="glow"><feGaussianBlur stdDeviation="0.8" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="glowStrong"><feGaussianBlur stdDeviation="1.5" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="starGlow"><feGaussianBlur stdDeviation="0.6" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <linearGradient id="shootingTrailGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="white" stopOpacity="0.9" />
            <stop offset="30%" stopColor={T.pink} stopOpacity="0.4" />
            <stop offset="100%" stopColor={T.purple} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Background twinkling stars */}
        {bgStars.map((s, i) => (
          <circle key={`star-${i}`} cx={s.x} cy={s.y} r={s.r} fill={i % 3 === 0 ? T.pink : T.purple}
            opacity="0" filter="url(#starGlow)"
            style={{ animation: `twinkle ${s.dur}s ease-in-out ${s.delay}s infinite` }} />
        ))}

        {/* Shooting stars with motion trail */}
        {shootingStars.map((s, i) => (
          <g key={`shoot-${i}`}>
            <circle r="0.15" fill="white" filter="url(#glow)">
              <animateMotion dur={`${s.dur}s`} repeatCount="indefinite" begin={`${s.delay}s`}
                path={`M${s.x1},${s.y1} L${s.x2},${s.y2}`} />
            </circle>
            <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke="url(#shootingTrailGrad)" strokeWidth="0.6"
              strokeDasharray="0.3 3" filter="url(#glow)" opacity="0"
              style={{ animation: `shootingStar ${s.dur}s ease-in ${s.delay}s infinite` }} />
          </g>
        ))}

        {/* Outer perimeter ring — pink glowing circle where servers orbit */}
        <circle cx="50" cy="50" r="32" fill="none"
          stroke={T.pink} strokeWidth="0.3" strokeOpacity="0.35"
          style={{ filter: `drop-shadow(0 0 4px ${T.pink}40)` }} />
        <circle cx="50" cy="50" r="32" fill="none"
          stroke={T.pink} strokeWidth="0.08" strokeDasharray="0.6 1.5" opacity="0.5"
          className="animate-spin" style={{ animationDuration: '45s', transformOrigin: '50px 50px' }} />

        {/* Middle ring — hexagon, processing perimeter */}
        <polygon points="50,20 76,35 76,65 50,80 24,65 24,35" fill="none"
          stroke="rgba(162,70,255,0.15)" strokeWidth="0.2"
          style={{ animation: 'spinReverse 18s linear infinite', transformOrigin: '50px 50px' }} />

        {/* Neural hexagon layers — 3 rotating hexagons creating brain depth */}
        {[
          { pts: '50,26 71,38 71,62 50,74 29,62 29,38', dur: 10, op: 0.12, sw: 0.25, col: '#06B6D4' },
          { pts: '50,30 68,40 68,60 50,70 32,60 32,40', dur: 14, op: 0.18, sw: 0.2, col: '#C084FC' },
          { pts: '50,22 73,36 73,64 50,78 27,64 27,36', dur: 22, op: 0.08, sw: 0.3, col: '#FF1493' },
        ].map((h, i) => (
          <polygon key={`nh-${i}`} points={h.pts} fill="none"
            stroke={h.col} strokeWidth={h.sw} strokeOpacity={h.op}
            strokeDasharray={i === 1 ? '1 0.5' : undefined}
            filter="url(#glow)"
            style={{ animation: `spinReverse ${h.dur}s linear ${i * 3}s infinite`, transformOrigin: '50px 50px' }} />
        ))}

        {/* Inner core — hexagon with pulse glow */}
        <polygon points="50,28 69,39 69,61 50,72 31,61 31,39" fill="url(#neuralGrad)" filter="url(#glow)"
          className="animate-pulse" style={{ animationDuration: '2.5s', opacity: 0.65 }} />

        {/* Neural wave propagation rings */}
        {[1, 2, 3].map((w, i) => (
          <circle key={`nw-${i}`} cx="50" cy="50" r="6" fill="none" stroke={i % 2 === 0 ? T.pink : '#06B6D4'} strokeWidth="0.3" opacity="0"
            style={{ animation: `neuralWave ${4 + i * 2}s ease-out ${i * 1.3}s infinite` }} />
        ))}

        {/* Orbital electrons — 4 AI particles orbiting the neural core */}
        {[
          { r: 22, dur: 8, delay: 0, size: 0.5, color: T.pink },
          { r: 18, dur: 6, delay: 2, size: 0.4, color: T.purple },
          { r: 26, dur: 11, delay: 4, size: 0.35, color: '#C084FC' },
          { r: 15, dur: 5, delay: 1, size: 0.3, color: '#06B6D4' },
        ].map((orb, i) => (
          <g key={`orb-${i}`} style={{ animation: `orbitalSpin ${orb.dur}s linear ${orb.delay}s infinite`, transformOrigin: '50px 50px' }}>
            <circle cx="50" cy={50 - orb.r} r={orb.size} fill={orb.color} filter="url(#glow)" opacity="0.8" />
            <circle cx="50" cy={50 - orb.r} r={orb.size * 2.5} fill="none" stroke={orb.color} strokeWidth="0.15" opacity="0.25" />
          </g>
        ))}

        {/* Neural synapse particles — data traveling along node connections */}
        {particles.map((p, i) => (
          <g key={`syn-${p.id}`}>
            <circle r="0.35" fill="#06B6D4" filter="url(#glow)" opacity="0"
              style={{ animation: `synapseFlow ${2 + i * 0.6}s ease-in-out ${i * 0.5}s infinite` }}>
              <animateMotion dur={`${2 + i * 0.6}s`} repeatCount="indefinite" begin={`${i * 0.5}s`}
                path={`M${p.from.x},${p.from.y} L50,50 L${p.to.x},${p.to.y}`} />
            </circle>
          </g>
        ))}

        {/* Connection lines — server nodes to core */}
        {ringNodes.map((n, i) => {
          const isOff = n.status === 'offline' || n.status === 'exited' || n.status === 'error';
          return (
            <line key={i} x1={n.x} y1={n.y} x2="50" y2="50"
              stroke={isOff ? 'rgba(100,100,100,0.15)' : 'url(#edgeGrad)'}
              strokeWidth="0.2" strokeDasharray={isOff ? '0.8 1.2' : undefined}
              opacity={isOff ? "0.3" : "0.5"}
              style={{ animation: `lineFlicker ${3 + i * 0.4}s ease-in-out infinite` }} />
          );
        })}

        {/* Inter-node connections */}
        {(() => {
          const lines = [];
          for (let i = 0; i < Math.min(edges || 0, totalNodes * 2, 16); i++) {
            const a = i % totalNodes;
            const b = (i + Math.floor(totalNodes / 3)) % totalNodes;
            if (a !== b && ringNodes[a] && ringNodes[b]) {
              lines.push({ a: ringNodes[a], b: ringNodes[b], id: i });
            }
          }
          return lines.map((l) => (
            <line key={`e-${l.id}`} x1={l.a.x} y1={l.a.y} x2={l.b.x} y2={l.b.y}
              stroke="rgba(162,70,255,0.08)" strokeWidth="0.15" strokeDasharray="1 2"
              style={{ animation: `glitchFlicker ${2 + l.id * 0.6}s ease-in-out ${l.id * 0.3}s infinite` }} />
          ));
        })()}

        {/* Data packet particles */}
        {particles.map((p) => (
          <g key={`pt-${p.id}`}>
            <circle r="0.6" fill={T.pink} filter="url(#glow)" opacity="0"
              style={{ animation: `particle${p.id % 4} ${3 + p.delay * 0.5}s linear ${p.delay}s infinite` }}>
              <animateMotion dur={`${3 + p.delay * 0.5}s`} repeatCount="indefinite" begin={`${p.delay}s`}
                path={`M50,50 L${p.to.x},${p.to.y}`} />
            </circle>
          </g>
        ))}

        {/* Core center glow — hexagonal neural center */}
        <polygon points="50,34 65,42 65,58 50,66 35,58 35,42" fill="url(#coreGlow)" filter="url(#glowStrong)"
          className="animate-pulse" style={{ animationDuration: '2s', opacity: 0.7 }} />
        {/* Central AI core node */}
        <circle cx="50" cy="50" r="2.5" fill={T.pink} filter="url(#glowStrong)"
          style={{ animation: 'neuralPulse 1.5s ease-in-out infinite' }} />

        {/* Server nodes on outer ring */}
        {ringNodes.map((n, i) => {
          const color = nodeStatusColor(n.status);
          const isOff = n.status === 'offline' || n.status === 'exited' || n.status === 'error';
          return (
            <g key={`nd-${i}`} style={{ cursor: 'pointer' }}>
              <title>{n.name} ({n.status || 'online'})</title>
              {/* Orbital trail ring */}
              <circle cx={n.x} cy={n.y} r="4" fill="none" stroke={color} strokeWidth="0.12" opacity="0.25" />
              {/* Main node */}
              <circle cx={n.x} cy={n.y} r={isOff ? "1.5" : "2.5"} fill={isOff ? '#3a3a3a' : T.pink}
                style={{ filter: isOff ? 'none' : `drop-shadow(0 0 5px ${T.pink}80)` }} />
              {/* Status glow ring */}
              <circle cx={n.x} cy={n.y} r="3.5" fill="none" stroke={color} strokeWidth="0.2"
                opacity={isOff ? "0.3" : "0.7"}
                style={{ animation: `pulseNode${i % 3} ${2 + i * 0.7}s ease-in-out infinite`,
                  filter: isOff ? 'none' : `drop-shadow(0 0 3px ${color}60)` }} />
              {/* Label */}
              <text x={n.x} y={n.y + 5.5} textAnchor="middle" fill={isOff ? '#555' : '#C0C0D0'}
                fontSize="1.7" fontWeight="600"
                style={{ fontFamily: 'monospace', paintOrder: 'stroke', stroke: T.bg, strokeWidth: '0.4px' }}>
                {n.name.length > 11 ? n.name.slice(0, 10) + '…' : n.name}
              </text>
            </g>
          );
        })}

        {/* CORE label */}
        <text x="50" y="47" textAnchor="middle" dominantBaseline="central"
          style={{ fill: T.textBright, fontSize: '2px', fontWeight: 800, letterSpacing: '0.15em', fontFamily: 'monospace' }}>
          SYNOTI
        </text>
        <text x="50" y="52" textAnchor="middle" dominantBaseline="central"
          style={{ fill: '#06B6D4', fontSize: '1.6px', fontWeight: 700, letterSpacing: '0.25em', fontFamily: 'monospace' }}>
          AI BRAIN
        </text>

        {/* Corner decorations — 4 corners */}
        {[
          { x: 2, y: 2, rot: 0 },
          { x: 98, y: 2, rot: 90 },
          { x: 98, y: 98, rot: 180 },
          { x: 2, y: 98, rot: 270 },
        ].map((c, i) => (
          <g key={`corner-${i}`} transform={`translate(${c.x},${c.y}) rotate(${c.rot})`}
            opacity="0.25" style={{ animation: `twinkle 3s ease-in-out ${i * 0.8}s infinite` }}>
            <path d="M0,0 L4,0 L4,0.4 L0.4,0.4 L0.4,4 L0,4 Z" fill={T.purple} filter="url(#glow)" />
          </g>
        ))}
      </svg>

      <div className="absolute bottom-1 left-1 right-1 flex justify-between text-[8px]" style={{ color: T.textDim }}>
        <span>{serverList.length} servers</span>
        <span>{edges || ringNodes.length} links</span>
        <span>{services.length} containers</span>
      </div>

      <style>{`
        @keyframes pulseNode0 { 0%,100% { opacity:0.7;r:2.2 } 50% { opacity:1;r:2.8 } }
        @keyframes pulseNode1 { 0%,100% { opacity:0.6;r:2 } 50% { opacity:0.9;r:2.6 } }
        @keyframes pulseNode2 { 0%,100% { opacity:0.8;r:2.4 } 50% { opacity:1;r:3 } }
        @keyframes spinReverse { from { transform: rotate(0deg); transform-origin: 50px 50px } to { transform: rotate(-360deg); transform-origin: 50px 50px } }
        @keyframes lineFlicker { 0%,100% { opacity:0.3 } 50% { opacity:0.7 } }
        @keyframes twinkle { 0%,100% { opacity:0 } 50% { opacity:0.8 } }
        @keyframes shootingStar { 0% { opacity:0 } 5% { opacity:1 } 15% { opacity:0 } 100% { opacity:0 } }
        @keyframes orbitalSpin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes glitchFlicker { 0%,100% { opacity:0.4 } 30% { opacity:0.8 } 60% { opacity:0.2 } 80% { opacity:0.7 } }
        @keyframes neuralWave { 0% { r:4;opacity:0.5 } 100% { r:32;opacity:0 } }
        @keyframes synapseFlow { 0% { opacity:0 } 20% { opacity:1 } 80% { opacity:0.6 } 100% { opacity:0 } }
        @keyframes neuralPulse { 0%,100% { r:2;opacity:0.6 } 50% { r:3.5;opacity:1 } }
      `}</style>
    </div>
  );
}

function NetworkDualLine({ history }) {
  if (!history || history.length < 2) return <div className="flex items-center justify-center text-[8px]" style={{ height: 32, color: T.textDim }}>collecting data...</div>;
  const max = Math.max(...history.map(h => Math.max(h.ingress || 0, h.egress || 0)), 1);
  const w = 100, h = 32;
  const ptsIngress = history.map((p, i) => `${(i / (history.length - 1)) * w},${h - ((p.ingress || 0) / max) * h}`).join(' ');
  const ptsEgress = history.map((p, i) => `${(i / (history.length - 1)) * w},${h - ((p.egress || 0) / max) * h}`).join(' ');
  return (
    <svg width="100%" height={h} viewBox="0 0 100 32" preserveAspectRatio="none" className="opacity-85">
      <polyline fill="none" stroke={T.pink} strokeWidth="1.2" points={ptsIngress}
        style={{ filter: `drop-shadow(0 0 2px ${T.pink}50)` }} />
      <polyline fill="none" stroke={T.purple} strokeWidth="1.2" points={ptsEgress}
        style={{ filter: `drop-shadow(0 0 2px ${T.purple}50)` }} />
    </svg>
  );
}

function PipelineCharts({ enhancedData, dashboardData, services, networkHistory }) {
  const kafka = enhancedData?.kafka_lag || {};
  const logVol = enhancedData?.log_volume || [];
  const queues = dashboardData?.queues || {};

  const kafkaTopics = Object.entries(kafka).filter(([k]) => k !== 'consumer_lag');

  const totalReqs = useMemo(() => services.reduce((sum, s) => sum + (s.requests || 0), 0), [services]);
  const logData = useMemo(() => {
    if (logVol.length > 0) return logVol.map(l => l.count || l.value || l.volume || 0).slice(-40);
    return [];
  }, [logVol]);

  const netIn = useMemo(() => networkHistory?.map(h => h.ingress / 1e6) || [], [networkHistory]);
  const netOut = useMemo(() => networkHistory?.map(h => h.egress / 1e6) || [], [networkHistory]);
  const netHistoryForChart = useMemo(() => networkHistory?.length > 1 ? networkHistory : null, [networkHistory]);

  return (
    <div className="grid grid-cols-1 gap-1.5">
      {kafkaTopics.length > 0 && (
        <div style={{ background: 'rgba(255,255,255,0.02)', padding: 8, borderRadius: 6 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
            <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: T.textDim }}>Kafka Message Queue</span>
            <span className="text-[8px] font-mono" style={{ color: T.purple }}>{kafkaTopics.length} topics</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {kafkaTopics.slice(0, 4).map(([topic, lag]) => (
              <div key={topic} className="flex items-center gap-2">
                <span className="text-[8px] w-16 truncate" style={{ color: T.text }}>{topic}</span>
                <div className="flex-1"><MiniBar value={Math.min(lag || 0, 1000)} max={1000} height={3} /></div>
                <span className="text-[8px] font-mono w-10 text-right" style={{ color: lag > 500 ? T.danger : lag > 100 ? T.warning : T.success }}>{lag}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: 'rgba(255,255,255,0.02)', padding: 8, borderRadius: 6 }}>
        <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: T.textDim }}>Queue Depth</span>
        <div className="grid grid-cols-3 gap-1" style={{ marginTop: 4 }}>
          {[
            { label: 'Incidents', value: queues.incidents_queued || 0 },
            { label: 'Tasks', value: queues.tasks_pending || 0 },
            { label: 'Alerts', value: queues.alerts_active || 0 },
          ].map(q => (
            <div key={q.label} className="text-center" style={{ background: 'rgba(255,255,255,0.015)', padding: 4, borderRadius: 6 }}>
               <div className="font-mono text-xs font-bold" style={{ color: T.pink }}>{formatNumber(q.value)}</div>
              <div className="text-[7px] uppercase tracking-wider" style={{ color: T.textDim }}>{q.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.02)', padding: 8, borderRadius: 6 }}>
        <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: T.textDim }}>Log Volume (24h)</span>
        <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
          <div className="text-center">
            <div className="font-mono text-xs font-bold" style={{ color: T.purple }}>
              {logVol.length > 0 ? logVol[logVol.length - 1]?.count || '--' : totalReqs > 0 ? totalReqs.toLocaleString() : '--'}
            </div>
            <div className="text-[7px] uppercase" style={{ color: T.textDim }}>
              {logVol.length > 0 ? 'Events/h' : 'req/s'}
            </div>
          </div>
          <div className="flex-1">
            {logData.length > 1 ? <SparkLine data={logData} height={26} /> : (
              <div className="text-[8px] text-center" style={{ color: T.textDim }}>
                {totalReqs > 0 ? `${totalReqs.toLocaleString()} total requests` : 'no log data'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.02)', padding: 8, borderRadius: 6 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
          <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: T.textDim }}>Network Throughput</span>
          <span className="text-[7px] font-mono" style={{ color: T.textDim }}>6h</span>
        </div>
        {netHistoryForChart ? (
          <>
            <NetworkDualLine history={netHistoryForChart} />
            <div className="flex justify-between" style={{ marginTop: 4 }}>
              <div className="flex items-center gap-1">
                <span className="flex-shrink-0" style={{ width: 6, height: 6, borderRadius: '50%', background: T.pink, boxShadow: `0 0 3px ${T.pink}` }} />
                <span className="text-[8px] font-mono" style={{ color: T.pink }}>
                  IN {netIn.length > 0 ? `${netIn[netIn.length - 1].toFixed(1)} Mbps` : '--'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="flex-shrink-0" style={{ width: 6, height: 6, borderRadius: '50%', background: T.purple, boxShadow: `0 0 3px ${T.purple}` }} />
                <span className="text-[8px] font-mono" style={{ color: T.purple }}>
                  OUT {netOut.length > 0 ? `${netOut[netOut.length - 1].toFixed(1)} Mbps` : '--'}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="text-[8px] text-center" style={{ color: T.textDim, padding: '4px 0' }}>collecting data...</div>
        )}
      </div>
    </div>
  );
}

export default function NOCCenterPanel({ topologyData, serverList, services, enhancedData, dashboardData, networkHistory }) {
  return (
    <div className="flex flex-col gap-1.5 overflow-y-auto h-full" style={{ scrollbarWidth: 'thin' }}>
      <div style={{ ...panelStyle, padding: 12 }}>
        <GradientTitle text="Network Topology" />
        <TopologyViz
          nodes={topologyData?.nodes?.length || serverList.length}
          edges={topologyData?.edges?.length}
          serverList={serverList}
          services={services}
        />
      </div>

      <div style={{ ...panelStyle, padding: 12 }}>
        <GradientTitle text="Data Pipeline" />
        <PipelineCharts enhancedData={enhancedData} dashboardData={dashboardData} services={services} networkHistory={networkHistory} />
      </div>
    </div>
  );
}
